<template>
  <template v-if="merchant && merchantId">
    <RevSkeleton
      v-if="isSellerRatingPending"
      data-test="item-seller-rating-skeleton"
      height="56px"
      shape="rectangle"
      width="100%"
    />

    <template v-else>
      <ReassuranceItem :href="sellerpageUrl" @click="onReassuranceItemClick">
        <template #icon>
          <IconOfficial class="h-24 w-24 md:h-32 md:w-32" />
        </template>

        <template #title>
          <div class="flex w-full min-w-0 flex-wrap items-center">
            <span class="min-w-0 break-words mr-4">
              {{
                i18n(translations.refurbishedBy, {
                  sellerName: merchant.company,
                })
              }}
            </span>
            <span
              class="caption inline-flex flex-wrap items-center gap-y-4 text-static-default-low"
            >
              <Flag
                v-if="showCountryFlag"
                :aria-label="merchant.country.name"
                class="shrink-0"
                :country-code="merchant.country.code as MarketCountryCode"
              />
              <span v-else class="shrink-0">
                ({{ merchant.country.name }})
              </span>
            </span>
          </div>
        </template>

        <template #subtitle>
          <div v-if="showRating" class="flex items-center gap-8">
            <RevRating
              v-if="rating !== undefined"
              :score="rating"
              size="extra-small"
            />
            <span v-if="reviewsCount !== undefined" class="caption">
              {{ i18n(translations.reviewsCount, { count: reviewsCount }) }}
            </span>
          </div>
        </template>
      </ReassuranceItem>

      <div v-if="showSellerDetailsModal" class="flex justify-center">
        <RevLink
          class="text-action-default-hi caption-link bg-transparent border-none cursor-pointer p-0"
          @click="openSellerDetailsModal"
        >
          {{ i18n(translations.viewComplianceInfo) }}
        </RevLink>
      </div>
    </template>

    <SellerDetailsModal v-if="showSellerDetailsModal" :listingId />
  </template>
</template>

<script lang="ts" setup>
import { useAsyncData } from '#imports'
import { computed, watch } from 'vue'

import { MarketplaceCode } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'
import Flag from '@backmarket/nuxt-layer-base/components/Flag/Flag.vue'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { RevRating } from '@ds/components/Rating'
import { RevSkeleton } from '@ds/components/Skeleton'
import { IconOfficial } from '@ds/icons/IconOfficial'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { MODAL_NAMES } from '~/scopes/product/constants'
import SellerDetailsModal from '~/scopes/product-core/components/presentationals/Seller/components/SellerDetailsModal/SellerDetailsModal.vue'
import { ROUTES as PRODUCT_LIST_ROUTES } from '~/scopes/product-list/route-names'

import ReassuranceItem from '../ReassuranceItem/ReassuranceItem.vue'

import translations from './ItemSellerRating.translations'

const props = withDefaults(
  defineProps<{
    merchant: NonNullable<GetPickersResponse['selectedOffer']>['merchant']
    listingId?: string | null
    tracking: Record<string, string>
  }>(),
  { listingId: null },
)

const i18n = useI18n()
const { trackClick } = useTracking()
const marketplace = useMarketplace()
const $httpFetch = useHttpFetch()
const logger = useLogger()
const resolveLocalizedRoute = useRouteLocationWithLocale()

const listingId = computed(() => props.listingId ?? null)
const merchantId = computed(() => props.merchant?.merchantId ?? null)

const sellerpageUrl = computed(() => {
  const { merchant } = props

  if (!merchant?.merchantUrl || !merchant?.merchantId) return undefined

  try {
    const { pathname } = new URL(merchant.merchantUrl)
    const merchantSlugMatch = pathname.match(/\/s\/([^/]+)\//)

    if (merchantSlugMatch?.[1]) {
      return resolveLocalizedRoute({
        name: PRODUCT_LIST_ROUTES.SELLER_PRODUCT_LIST,
        params: {
          merchantName: merchantSlugMatch[1],
          uuid: merchant.merchantId,
        },
      })
    }
  } catch {
    return merchant.merchantUrl
  }

  return merchant.merchantUrl
})

const {
  data: sellerRatingData,
  pending: isSellerRatingPending,
  error: sellerRatingError,
} = useAsyncData<Rating | null>(
  () => `seller-rating-pdp-${merchantId.value ?? 'none'}`,
  () => {
    if (!merchantId.value) return Promise.resolve(null)

    return $httpFetch('/reviews/v1/sellers/:uuid/products/reviews-summary', {
      pathParams: { uuid: merchantId.value },
    })
  },
  {
    server: false,
    lazy: true,
    watch: [merchantId],
  },
)

watch(sellerRatingError, () => {
  if (!sellerRatingError.value) return

  logger.error('[PDP Seller Rating] Failed to fetch', {
    error: sellerRatingError.value,
    owners: ['bot-squad-content-front'],
  })
})

const rating = computed(() => sellerRatingData.value?.averageRate)
const reviewsCount = computed(() => sellerRatingData.value?.count)

const showSellerDetailsModal = computed(
  () => marketplace.marketplace.marketplaceCode === MarketplaceCode.EU,
)
const showRating = computed(
  () => rating.value !== undefined && reviewsCount.value !== 0,
)

const showCountryFlag = computed(() => {
  return (
    !!props.merchant?.country.code &&
    [MarketCountryCode.GB, MarketCountryCode.US, MarketCountryCode.AU].includes(
      marketplace.market.countryCode,
    )
  )
})

function onReassuranceItemClick() {
  trackClick(props.tracking)
}

function openSellerDetailsModal(event: MouseEvent) {
  openModal(MODAL_NAMES.SELLER_DETAILS, event)
  trackClick({ ...props.tracking, name: 'view_compliance_info' })
}
</script>
